/**
 * Smart Recommendations - 4-slot intelligent product recommendations
 * Slot types: complete_the_look, similar_style, best_seller, strategic_push
 * All slots enforce gender matching as a hard rule.
 */
(function () {
  const CACHE_KEY = "smartRec_cache";
  const CACHE_TTL_COLLECTION = 30 * 60 * 1000; // 30 minutes — collections rarely change
  const CACHE_TTL_RECS = 15 * 60 * 1000; // 15 minutes — ML recommendations update periodically
  const CACHE_TTL_TILE = 15 * 60 * 1000; // 15 minutes — tile HTML (price/availability can change)

  class SmartRecommendations {
    constructor(container) {
      this.container = container;
      this.productData = this._parseProductData();
      if (!this.productData) return;

      this.slots = Array.from(container.querySelectorAll("[data-smart-slot]"));
      this.sectionId = container.dataset.sectionId;
      this.usedProductIds = new Set([this.productData.id]);
      this.init();
    }

    _parseProductData() {
      const el = this.container.querySelector("[data-smart-product-json]");
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error("SmartRec: Failed to parse product data", e);
        return null;
      }
    }

    async init() {
      try {
        // Pre-fetch ALL data sources in parallel
        const prefetches = [
          this._fetchAllCandidates(),
          this._fetchRecommendations("related", 20),
        ];
        // Read slot configs and pre-fetch best-seller / strategic collections
        for (const slot of this.slots) {
          const type = slot.dataset.slotType;
          if (type === "best_seller") {
            const handle = slot.dataset.bestSellerCollection || "";
            const limit = parseInt(slot.dataset.bestSellerLimit, 10) || 10;
            prefetches.push(this._fetchBestSellers(handle, limit));
          }
          if (type === "strategic_push" && slot.dataset.strategyCollection) {
            prefetches.push(
              this._fetchStrategicData(slot.dataset.strategyCollection),
            );
          }
        }
        const [candidates, relatedRecs] = await Promise.all(prefetches);
        this._relatedRecs = relatedRecs;
        const genderFiltered = this._filterByGender(candidates);

        // Progressive pick + render: render each slot immediately after picking
        // (picks are sequential for usedProductIds, but renders fire in parallel)
        const renderPromises = [];
        for (const slot of this.slots) {
          const type = slot.dataset.slotType;
          let product = await this._pickForSlot(type, genderFiltered, slot);
          if (!product) {
            product = await this._fetchFallback();
          }
          if (!product) {
            product =
              genderFiltered.find((p) => !this.usedProductIds.has(p.id)) ||
              null;
          }
          if (product) {
            this.usedProductIds.add(product.id);
            // Fire render immediately, don't await — runs while next slot picks
            renderPromises.push(this._renderProduct(slot, product));
          } else {
            slot.style.display = "none";
          }
        }

        // Wait for all renders to complete before initializing slider
        await Promise.all(renderPromises);
        this._initSlider();
      } catch (err) {
        console.error("SmartRec: initialization error", err);
      }
    }

    // ──────────── DATA FETCHING ────────────

    async _fetchAllCandidates() {
      const collections = this._getCollectionsToFetch();
      const allProducts = [];
      const seen = new Set();

      const fetches = collections.map((handle) =>
        this._fetchCollection(handle),
      );
      const results = await Promise.all(fetches);

      for (const products of results) {
        for (const p of products) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            allProducts.push(p);
          }
        }
      }

      return allProducts;
    }

    _getCollectionsToFetch() {
      const handles = new Set(["all"]);

      // Add strategy collections from slots
      for (const slot of this.slots) {
        const col = slot.dataset.strategyCollection;
        if (col) handles.add(col);
      }

      // Add product's own collections if available
      if (this.productData.collections) {
        for (const c of this.productData.collections) {
          handles.add(c);
        }
      }

      return Array.from(handles);
    }

    /**
     * Fetch products from a collection via lightweight Liquid template.
     * Returns only fields needed for picking (no images, body_html, options).
     * Paginated at 250 per page via Liquid paginate block.
     */
    async _fetchCollection(handle) {
      const cacheKey = `${CACHE_KEY}_${handle}`;
      const cached = this._getCache(cacheKey, CACHE_TTL_COLLECTION);
      if (cached) return cached;

      try {
        const resp = await fetch(
          `/collections/${encodeURIComponent(handle)}?view=smart-rec-candidates`,
        );
        if (!resp.ok) return [];
        const data = await resp.json();
        const products = data.products || [];

        this._setCache(cacheKey, products);
        return products;
      } catch (e) {
        console.error(`SmartRec: Failed to fetch collection ${handle}`, e);
        return [];
      }
    }

    /**
     * Fetch best sellers from a pre-sorted collection via lightweight Liquid template.
     * Sort order is determined by the collection's default sort in Admin.
     * Default collection: "best-sellers" (configurable via Theme Editor).
     */
    async _fetchBestSellers(handle, limit) {
      const collectionHandle = handle || "best-sellers";
      const cacheKey = `${CACHE_KEY}_bestselling_${collectionHandle}_${limit}`;
      const cached = this._getCache(cacheKey, CACHE_TTL_COLLECTION);
      if (cached) return cached;

      try {
        const resp = await fetch(
          `/collections/${encodeURIComponent(collectionHandle)}?view=smart-rec-candidates`,
        );
        if (!resp.ok) return [];
        const data = await resp.json();
        // Trim to requested limit (Liquid template returns up to 250)
        const products = (data.products || []).slice(0, limit);
        this._setCache(cacheKey, products);
        return products;
      } catch (e) {
        console.error(
          `SmartRec: Failed to fetch best sellers from ${collectionHandle}`,
          e,
        );
        return [];
      }
    }

    /**
     * Fetch Shopify Product Recommendations API.
     * @param {'related'|'complementary'} intent - related = similar, complementary = cross-sell
     * @param {number} limit
     */
    async _fetchRecommendations(intent = "related", limit = 10) {
      const cacheKey = `${CACHE_KEY}_recs_${this.productData.id}_${intent}`;
      const cached = this._getCache(cacheKey, CACHE_TTL_RECS);
      if (cached) return cached;

      try {
        const resp = await fetch(
          `/recommendations/products.json?product_id=${this.productData.id}&limit=${limit}&intent=${intent}`,
        );
        if (!resp.ok) return [];
        const data = await resp.json();
        const products = data.products || [];

        // Normalize prices: Recommendations API returns variant prices in
        // subunits (like Liquid product.price), but Collections API returns
        // main units. Divide by 100 to align with Collections API format.
        for (const p of products) {
          if (p.variants) {
            for (const v of p.variants) {
              if (v.price) v.price = (parseFloat(v.price) / 100).toString();
            }
          }
        }

        this._setCache(cacheKey, products);
        return products;
      } catch (e) {
        console.error(
          `SmartRec: Failed to fetch recommendations (intent=${intent})`,
          e,
        );
        return [];
      }
    }

    async _fetchFallback() {
      try {
        const products = this._relatedRecs || await this._fetchRecommendations("related", 10);
        const available = products.filter(
          (p) => !this.usedProductIds.has(p.id),
        );
        const genderFiltered = this._filterByGender(available);
        return genderFiltered[0] || null;
      } catch (e) {
        return null;
      }
    }

    // ──────────── GENDER FILTER (HARD RULE) ────────────

    _filterByGender(products) {
      const currentGender = this.productData.gender; // 'Male', 'Female', 'Unisex'
      if (!currentGender) return products;

      return products.filter((p) => {
        const rawTags =
          typeof p.tags === "string"
            ? p.tags.split(", ").map((t) => t.trim().toLowerCase())
            : (p.tags || []).map((t) => t.trim().toLowerCase());
        const genderTag = rawTags.find((t) => t.startsWith("target gender:"));
        if (!genderTag) return false;

        const productGender = genderTag.replace("target gender:", "").trim();
        const current = currentGender.toLowerCase();

        // Unisex candidate matches any gendered current product,
        // but Unisex current product only matches Unisex candidates
        if (current === "unisex") return productGender === "unisex";
        return productGender === current || productGender === "unisex";
      });
    }

    // ──────────── SLOT PICKING LOGIC ────────────

    async _pickForSlot(type, candidates, slot) {
      // Exclude already used products
      const available = candidates.filter(
        (p) => !this.usedProductIds.has(p.id),
      );

      switch (type) {
        case "complete_the_look":
          return this._pickCompleteTheLook(available, slot);
        case "similar_style":
          return this._pickSimilarStyle(available, slot);
        case "best_seller":
          return this._pickBestSeller(available, slot);
        case "strategic_push":
          return this._pickStrategicPush(available, slot);
        default:
          return available[0] || null;
      }
    }

    /**
     * SLOT 1: Complete the Look (Cross-sell)
     * - Category must DIFFER from current product
     * - Price >= current product price
     * - Shares season or collection tag
     */
    _pickCompleteTheLook(candidates, slot) {
      const currentCategory = this.productData.category;
      const currentPrice = this._getCurrentPrice();
      const currentSeasonTags = this.productData.seasonTags || [];
      const currentCollectionTags = this.productData.collectionTags || [];

      // Read filter toggles from Theme Editor checkboxes
      const useSeasonFilter = slot.dataset.filterSeasonTag !== "false";
      const useCollectionFilter = slot.dataset.filterCollectionTag !== "false";
      const priceOffsetPct = parseInt(slot.dataset.priceOffsetPercent, 10) || 0;
      const priceMin = priceOffsetPct > 0 ? currentPrice * (1 - priceOffsetPct / 100) : currentPrice;
      const priceMax = priceOffsetPct > 0 ? currentPrice * (1 + priceOffsetPct / 100) : Infinity;

      // Build active tag lists based on checkboxes
      const activeSeasonTags = useSeasonFilter ? currentSeasonTags : [];
      const activeCollectionTags = useCollectionFilter
        ? currentCollectionTags
        : [];

      const scored = [];
      let fallbackBest = null;

      for (const p of candidates) {
        const tags = this._parseTags(p);
        const price = this._getCandidatePrice(p);

        // Hard Rule 1: Category must differ
        if (currentCategory && tags.category === currentCategory) continue;
        if (!tags.category) continue;

        // Track fallback: different category, highest price (no other filters)
        if (!fallbackBest || price > this._getCandidatePrice(fallbackBest)) {
          fallbackBest = p;
        }

        // Hard Rule 2: Price within ±offset%
        if (!price || price < priceMin || price > priceMax) continue;

        // Hard Rule 3: Must share at least 1 active season OR collection tag
        if (activeSeasonTags.length > 0 || activeCollectionTags.length > 0) {
          const hasSharedTag =
            activeSeasonTags.some((st) => tags.allTags.includes(st)) ||
            activeCollectionTags.some((ct) => tags.allTags.includes(ct));
          if (!hasSharedTag) continue;
        }

        // Score by tag overlap
        let score = 0;
        for (const st of activeSeasonTags) {
          if (tags.allTags.includes(st)) score += 2;
        }
        for (const ct of activeCollectionTags) {
          if (tags.allTags.includes(ct)) score += 1;
        }
        scored.push({ product: p, score });
      }

      scored.sort((a, b) => b.score - a.score);

      // Pick randomly among candidates tied with the top score
      let winner = null;
      if (scored.length > 0) {
        const topScore = scored[0].score;
        const tied = scored.filter((s) => s.score === topScore);
        winner = tied[Math.floor(Math.random() * tied.length)];
      } else if (fallbackBest) {
        winner = { product: fallbackBest, score: 0 };
      }

      return winner ? winner.product : null;
    }

    /**
     * SLOT 2: Similar Style (CVR Protector)
     * Primary: Shopify Recommendations API (intent=related) — ML-based similarity
     * Fallback: Collection-based candidates
     * Filters: same category, same fit, price ±20%, >70% size availability
     */
    async _pickSimilarStyle(candidates, slot) {
      const currentCategory = this.productData.category;
      const currentFit = this.productData.fit;
      const currentPrice = this._getCurrentPrice();
      const priceMin = currentPrice * 0.8;
      const priceMax = currentPrice * 1.2;

      // Read filter toggle from Theme Editor checkbox
      const useFitFilter = slot.dataset.filterFitTag !== "false";

      const similarStyleFilter = (products) => {
        return products.filter((p) => {
          if (this.usedProductIds.has(p.id)) return false;
          const tags = this._parseTags(p);

          // Same category
          if (currentCategory && tags.category !== currentCategory)
            return false;

          // Same fit (only if filter enabled)
          if (useFitFilter && currentFit && tags.fit !== currentFit)
            return false;

          // Price within ±20%
          const price = this._getCandidatePrice(p);
          if (price > 0 && (price < priceMin || price > priceMax)) return false;

          // Inventory >70% size availability
          if (p.variants) {
            const total = p.variants.length;
            const available = p.variants.filter((v) => v.available).length;
            if (total > 0 && available / total < 0.7) return false;
          }

          return true;
        });
      };

      // PRIMARY: Shopify Recommendations API (ML-based similarity)
      const recommended = this._relatedRecs || await this._fetchRecommendations("related", 20);
      const recGenderFiltered = this._filterByGender(recommended);
      const recMatches = similarStyleFilter(recGenderFiltered);

      if (recMatches.length > 0) {
        // Sort by price proximity — Shopify API already ranks by relevance,
        // so among filtered results, prefer closest price
        recMatches.sort((a, b) => {
          const priceA = this._getCandidatePrice(a);
          const priceB = this._getCandidatePrice(b);
          return (
            Math.abs(priceA - currentPrice) - Math.abs(priceB - currentPrice)
          );
        });
        return recMatches[0];
      }

      // FALLBACK: Collection-based candidates
      const fallbackMatches = similarStyleFilter(candidates);

      fallbackMatches.sort((a, b) => {
        const priceA = this._getCandidatePrice(a);
        const priceB = this._getCandidatePrice(b);
        return (
          Math.abs(priceA - currentPrice) - Math.abs(priceB - currentPrice)
        );
      });

      return fallbackMatches[0] || null;
    }

    /**
     * SLOT 3: Best Seller (Social Proof)
     * - Top N from best-selling sorted collection (proxy for Revenue/CVR rank)
     * - Exclude currently viewed product
     * - In-stock in >=3 core sizes
     * - Gender must match (applied via dedicated fetch + filter)
     */
    async _pickBestSeller(candidates, slot) {
      const collectionHandle = slot.dataset.bestSellerCollection || "";
      const limit = parseInt(slot.dataset.bestSellerLimit, 10) || 10;

      // Fetch products sorted by best-selling (Shopify sorts by revenue)
      const bestSellers = await this._fetchBestSellers(collectionHandle, limit);
      const genderFiltered = this._filterByGender(bestSellers);

      let matches = genderFiltered.filter((p) => {
        // Exclude current product and already used
        if (this.usedProductIds.has(p.id)) return false;

        // In-stock >=3 core sizes
        const availableCount = p.variants
          ? p.variants.filter((v) => v.available).length
          : 0;
        return availableCount >= 3;
      });

      // Primary: best-selling sort order (first match = highest revenue)
      if (matches[0]) return matches[0];

      // Fallback: check general candidates for 'best-seller' tag
      return (
        candidates.find((p) => {
          const tags = this._parseTags(p);
          if (!tags.allTags.includes("best-seller")) return false;
          const fallbackAvailable = p.variants
            ? p.variants.filter((v) => v.available).length
            : 0;
          return fallbackAvailable >= 3;
        }) || null
      );
    }

    /**
     * SLOT 4: Strategic Push (Inventory Control)
     * Strategy options: overstock, collection_sale, slow_moving
     * Uses metafields custom.inventory_age (days) and custom.gross_margin (%)
     * via alternate collection template (?view=smart-rec-strategic).
     * Falls back to created_at / price proxies if metafields not set.
     */
    async _pickStrategicPush(candidates, slot) {
      const strategy = slot.dataset.strategy || "overstock";
      const strategyCollection = slot.dataset.strategyCollection;

      switch (strategy) {
        case "collection_sale": {
          if (strategyCollection) {
            return await this._pickFromStrategicCollection(strategyCollection);
          }
          return this._randomFromTop(this._filterStrategicCandidates(candidates), 5);
        }

        case "slow_moving": {
          // Try fetching metafield data from strategic collection
          let pool = this._filterStrategicCandidates(candidates);
          if (strategyCollection) {
            const metafieldProducts =
              await this._fetchStrategicData(strategyCollection);
            if (metafieldProducts.length > 0) {
              pool = metafieldProducts.filter(
                (p) =>
                  !this.usedProductIds.has(p.id) &&
                  this._isValidStrategicProduct(p),
              );
            }
          }

          // Primary: products with inventory_age 30–50 days (from metafield)
          let slowMoving = pool.filter((p) => {
            const age = p.inventory_age || 0;
            return age >= 30 && age <= 50;
          });

          if (slowMoving.length === 0) {
            // Fallback: products tagged 'slow-moving'
            slowMoving = pool.filter((p) => {
              const tags = this._parseTags(p);
              return tags.allTags.includes("slow-moving");
            });
          }

          if (slowMoving.length === 0) {
            // Last fallback: oldest products by created_at (30-50 days)
            const now = Date.now();
            const thirtyDays = 30 * 24 * 60 * 60 * 1000;
            const fiftyDays = 50 * 24 * 60 * 60 * 1000;

            slowMoving = pool.filter((p) => {
              if (!p.created_at) return false;
              const age = now - new Date(p.created_at).getTime();
              return age >= thirtyDays && age <= fiftyDays;
            });
          }

          // Sort by gross_margin desc if available
          slowMoving.sort(
            (a, b) => (b.gross_margin || 0) - (a.gross_margin || 0),
          );
          return this._randomFromTop(slowMoving, 5);
        }

        case "overstock":
        default: {
          // Try fetching metafield data from strategic collection
          let pool = this._filterStrategicCandidates(candidates);
          if (strategyCollection) {
            const metafieldProducts =
              await this._fetchStrategicData(strategyCollection);
            if (metafieldProducts.length > 0) {
              pool = metafieldProducts.filter(
                (p) =>
                  !this.usedProductIds.has(p.id) &&
                  this._isValidStrategicProduct(p),
              );
            }
          }

          // Sort by: inventory_age DESC (oldest stock first), then gross_margin DESC
          pool.sort((a, b) => {
            const ageA = a.inventory_age || 0;
            const ageB = b.inventory_age || 0;

            // If either has metafield data, use it
            if (ageA > 0 || ageB > 0) {
              if (ageA !== ageB) return ageB - ageA; // higher age first
              return (b.gross_margin || 0) - (a.gross_margin || 0); // higher margin first
            }

            // Fallback: created_at (older first) + price (higher first)
            const dateA = a.created_at
              ? new Date(a.created_at).getTime()
              : Date.now();
            const dateB = b.created_at
              ? new Date(b.created_at).getTime()
              : Date.now();
            if (dateA !== dateB) return dateA - dateB;

            const priceA = this._getCandidatePrice(a);
            const priceB = this._getCandidatePrice(b);
            return priceB - priceA;
          });

          return this._randomFromTop(pool, 5);
        }
      }
    }

    /**
     * Pick a random product from the top N of a sorted array.
     */
    _randomFromTop(sorted, n) {
      if (sorted.length === 0) return null;
      const top = sorted.slice(0, Math.min(n, sorted.length));
      return top[Math.floor(Math.random() * top.length)];
    }

    /**
     * Quality filter for Strategic Push candidates.
     * Excludes: gift cards, unavailable products, products with <2 variants (non-apparel).
     */
    _filterStrategicCandidates(candidates) {
      return candidates.filter((p) => {
        if (this.usedProductIds.has(p.id)) return false;
        return this._isValidStrategicProduct(p);
      });
    }

    _isValidStrategicProduct(p) {
      // Exclude gift cards
      if (p.product_type && p.product_type.toLowerCase() === "gift card")
        return false;
      const title = (p.title || "").toLowerCase();
      if (title.includes("gift card") || title.includes("gift wrap"))
        return false;

      // Must have multiple variants (apparel has sizes)
      const variantCount = p.variants ? p.variants.length : 0;
      if (variantCount < 2) return false;

      // Must be available (at least 1 variant in stock)
      const availableCount = p.variants
        ? p.variants.filter((v) => v.available).length
        : 0;
      if (availableCount === 0) return false;

      return true;
    }

    /**
     * Fetch products with metafield data (inventory_age, gross_margin)
     * via alternate collection template.
     */
    async _fetchStrategicData(handle) {
      const cacheKey = `${CACHE_KEY}_strategic_${handle}`;
      const cached = this._getCache(cacheKey, CACHE_TTL_COLLECTION);
      if (cached) return cached;

      try {
        const resp = await fetch(
          `/collections/${encodeURIComponent(handle)}?view=smart-rec-strategic`,
        );
        if (!resp.ok) return [];
        const data = await resp.json();
        const products = data.products || [];

        // Apply gender filter
        const filtered = this._filterByGender(products);
        this._setCache(cacheKey, filtered);
        return filtered;
      } catch (e) {
        console.error(
          `SmartRec: Failed to fetch strategic data from ${handle}`,
          e,
        );
        return [];
      }
    }

    async _pickFromStrategicCollection(handle) {
      // Try metafield-enriched data first
      const strategic = await this._fetchStrategicData(handle);
      if (strategic.length > 0) {
        const available = strategic.filter(
          (p) => !this.usedProductIds.has(p.id),
        );
        if (available.length > 0) {
          // Sort by gross_margin DESC
          available.sort(
            (a, b) => (b.gross_margin || 0) - (a.gross_margin || 0),
          );
          return this._randomFromTop(available, 5);
        }
      }

      // Fallback to standard collection fetch
      const products = await this._fetchCollection(handle);
      const genderFiltered = this._filterByGender(products);
      const available = genderFiltered.filter(
        (p) => !this.usedProductIds.has(p.id),
      );
      return this._randomFromTop(available, 5);
    }

    // ──────────── PRICE HELPERS ────────────

    /**
     * Get current product price (presentment currency, main units).
     * Liquid: product.price / 100 = main units.
     * API products.json also returns presentment currency main units.
     * Both are in the same currency — safe to compare directly.
     */
    _getCurrentPrice() {
      return this.productData.price;
    }

    /**
     * Get candidate product price (presentment currency, main units from API).
     */
    _getCandidatePrice(product) {
      if (product.variants && product.variants[0]) {
        return parseFloat(product.variants[0].price);
      }
      return 0;
    }

    // ──────────── TAG PARSING ────────────

    _parseTags(product) {
      if (product._parsedTags) return product._parsedTags;

      const rawTags =
        typeof product.tags === "string"
          ? product.tags.split(", ").map((t) => t.trim().toLowerCase())
          : (product.tags || []).map((t) => t.trim().toLowerCase());

      let category = null;
      let fit = null;
      const seasonTags = [];
      const collectionTags = [];

      for (const tag of rawTags) {
        if (tag.startsWith("category:")) {
          category = tag.replace("category:", "").trim();
        } else if (tag.startsWith("fit:")) {
          fit = tag.replace("fit:", "").trim();
        } else if (tag.startsWith("season:")) {
          seasonTags.push(tag);
        } else if (tag.startsWith("collection:")) {
          collectionTags.push(tag);
        }
      }

      product._parsedTags = { category, fit, seasonTags, collectionTags, allTags: rawTags };
      return product._parsedTags;
    }

    // ──────────── RENDERING ────────────

    async _renderProduct(slot, product) {
      const handle = product.handle;
      if (!handle) {
        slot.style.display = "none";
        return;
      }

      try {
        // Check tile HTML cache first
        const tileCacheKey = `${CACHE_KEY}_tile_${handle}`;
        let html = this._getCache(tileCacheKey, CACHE_TTL_TILE);

        if (!html) {
          const resp = await fetch(
            `/products/${encodeURIComponent(handle)}?section_id=smart-rec-item`,
          );
          if (!resp.ok) {
            slot.style.display = "none";
            return;
          }
          html = await resp.text();
          this._setCache(tileCacheKey, html);
        }

        const temp = document.createElement("div");
        temp.innerHTML = html;
        const content = temp.querySelector("[data-smart-rec-item]");

        const productContainer = slot.querySelector("[data-slot-product]");
        if (productContainer && content) {
          // Batch DOM writes in a single frame to avoid layout thrashing
          requestAnimationFrame(() => {
            productContainer.innerHTML = content.innerHTML;
            slot.classList.add("is-loaded");
            slot.classList.remove("is-loading");

            // Initialize product tile behaviors (quick shop, swatches, etc.)
            const tiles = productContainer.querySelectorAll(
              "[data-product-tile]",
            );
            tiles.forEach((tile) => {
              if (typeof ProductTitle === "function") {
                ProductTitle(tile);
              }
            });
          });
        }
      } catch (e) {
        console.error("SmartRec: render error for", handle, e);
        slot.style.display = "none";
      }
    }

    _initSlider() {
      const slider = this.container.querySelector("[data-smart-slider]");
      if (!slider) return;

      const visibleSlots = this.slots.filter((s) => s.style.display !== "none");
      if (visibleSlots.length <= 1) return;

      // Use Flickity if available (same as theme)
      if (typeof Flickity !== "undefined") {
        this._flickity = new Flickity(slider, {
          adaptiveHeight: true,
          pageDots: false,
          prevNextButtons: false,
          watchCSS: true,
          wrapAround: false,
        });
      }
    }

    // ──────────── CACHING ────────────

    _getCache(key, ttl) {
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts > ttl) {
          sessionStorage.removeItem(key);
          return null;
        }
        return data;
      } catch (e) {
        return null;
      }
    }

    _setCache(key, data) {
      try {
        sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
      } catch (e) {
        // Storage full or unavailable
      }
    }
  }

  // ──────────── INITIALIZATION ────────────

  // The section sits below the product details, and its collection fetches compete
  // with the page's own requests, so each section starts when it nears the viewport.
  function initWhenNear(section) {
    if (!("IntersectionObserver" in window)) {
      new SmartRecommendations(section);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        new SmartRecommendations(section);
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(section);
  }

  function initSmartRecommendations() {
    const sections = document.querySelectorAll(
      '[data-section-type="smart-recommendations"]',
    );
    sections.forEach(initWhenNear);
  }

  // Support both DOMContentLoaded and Shopify section events
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSmartRecommendations);
  } else {
    initSmartRecommendations();
  }

  // Reinitialize on Shopify section load (Theme Editor)
  document.addEventListener("shopify:section:load", function (e) {
    const section = e.target.querySelector(
      '[data-section-type="smart-recommendations"]',
    );
    if (section) new SmartRecommendations(section);
  });
})();
